import L from "leaflet";
import { KIND_COLORS, type Pin, type PinKind } from "@tarkov-callout/shared";

/**
 * Pins are plain DOM, not canvas, so the direction cone keeps its size and
 * angle in screen space no matter how far the map is zoomed.
 *
 * `heading` is measured clockwise from the top of the rendered map. The map is
 * never rotated by the viewer, so the same number means the same direction for
 * everyone in the room.
 *
 * Two colours drive the markup. `--pin-color` is the kind's colour and paints
 * everything you read at a distance: the dot and the direction cone. The
 * author's colour rides along as `--member-color` on the dot's ring, so "who
 * placed this" survives without competing with "what is it".
 */

/** U+FE0E keeps these as glyphs instead of colour emoji on some platforms. */
const GLYPHS: Record<PinKind, string> = {
  objective: "◎",
  rally: "⚑︎",
  enemy: "✕",
  loot: "◆",
  extract: "⇱",
  danger: "!",
  self: "●",
  draw: "✎",
};

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[char] ?? char,
  );
}

/** A wedge, a shaft and an arrowhead, all inheriting the member's colour. */
function coneMarkup(heading: number): string {
  return `<svg class="pin__cone" viewBox="-50 -50 100 100" style="transform:rotate(${heading.toFixed(1)}deg)" aria-hidden="true">
    <path class="pin__cone-wedge" d="M0 0 L-17 -44 A47 47 0 0 1 17 -44 Z" />
    <path class="pin__cone-shaft" d="M0 -6 L0 -34" />
    <path class="pin__cone-head" d="M0 -46 L-7 -32 L7 -32 Z" />
  </svg>`;
}

/**
 * A self marker means "this is me" and a stroke means "I drew this", so both
 * keep the member's colour. Every other kind is coloured by what it means.
 */
export function pinKindColor(kind: PinKind, memberColor: string): string {
  return kind === "self" || kind === "draw" ? memberColor : KIND_COLORS[kind];
}

export function pinIcon(pin: Pin, isMine: boolean): L.DivIcon {
  const classes = ["pin", `pin--${pin.kind}`];
  if (isMine) classes.push("pin--mine");
  if (pin.expiresAt !== null) classes.push("pin--transient");

  const caption = pin.label
    ? `<span class="pin__label">${escapeHtml(pin.label)}</span>`
    : "";
  const author = `<span class="pin__author">${escapeHtml(pin.authorName)}</span>`;

  const style = [
    `--pin-color:${escapeHtml(pinKindColor(pin.kind, pin.color))}`,
    `--member-color:${escapeHtml(pin.color)}`,
  ].join(";");

  const html = `<div class="${classes.join(" ")}" style="${style}">
    ${pin.heading === null ? "" : coneMarkup(pin.heading)}
    <div class="pin__dot">${GLYPHS[pin.kind]}</div>
    <div class="pin__caption">${caption}${author}</div>
  </div>`;

  return L.divIcon({
    html,
    className: "pin-icon",
    // Zero size so Leaflet anchors the element at the exact coordinate and our
    // own CSS centring decides how it sits around that point.
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

export function pinKindGlyph(kind: PinKind): string {
  return GLYPHS[kind];
}
